import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import $ from "jquery";
import { gsap } from "gsap";

import ApiURl from "../controllers/Api";
import imgLocation from "../controllers/imagePath";

const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
    <rect width="100%" height="100%" fill="#f2f2f2"/>
    <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
      font-family="Arial" font-size="18" fill="#777">
      No Image
    </text>
  </svg>
`);

async function postPhp(url, payload) {
  try {
    const res = await $.ajax({
      url,
      method: "POST",
      contentType: "application/json",
      dataType: "json",
      data: JSON.stringify(payload),
    });
    return res;
  } catch (jsonErr) {
    const res = await $.ajax({
      url,
      method: "POST",
      dataType: "json",
      data: payload,
    });
    return res;
  }
}

const ProductItems = ({ products = [] }) => {
  const [wishlistAdded, setWishlistAdded] = useState({});
  const [wishlistBusy, setWishlistBusy] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("Item Added to Wishlist");

  const [productList, setProductList] = useState(Array.isArray(products) ? products : []);
  const [productDiscountInfo, setProductDiscountInfo] = useState({});
  const [discountInfoLoaded, setDiscountInfoLoaded] = useState(false);

  const cardsRef = useRef({});

  useEffect(() => {
    setProductList(Array.isArray(products) ? products : []);
    setDiscountInfoLoaded(false); // Reset when products change
  }, [products]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      setWishlistAdded({});
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await $.getJSON(`${ApiURl}/getWishlistItems.php?user_id=${encodeURIComponent(userId)}`);
        const list = res?.wishlist || [];
        const map = {};
        for (const item of list) {
          if (item?.product_id != null) {
            map[String(item.product_id)] = true;
          }
        }
        if (!cancelled) setWishlistAdded(map);
      } catch (err) {
        console.error("Error loading wishlist:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch discount info for products
  useEffect(() => {
    let cancelled = false;

    const ids = Array.from(
      new Set(
        (productList || [])
          .map((p) => String(p?.product_id ?? ""))
          .filter(Boolean)
      )
    );

    if (ids.length === 0) {
      setDiscountInfoLoaded(true);
      return;
    }

    (async () => {
      try {
        const results = await Promise.all(
          ids.map(async (id) => {
            try {
              const response = await $.getJSON(
                `${ApiURl}/getProductPrices.php?product_id=${encodeURIComponent(id)}`
              );

              if (response?.success && response.summary) {
                return { id, ...response.summary };
              }
              return { id, prices: '', single_discount_types: '', single_discount_values: '', min_price: 0, has_discount: false };
            } catch (e) {
              console.error(`Error fetching price for product ${id}:`, e);
              return { id, prices: '', single_discount_types: '', single_discount_values: '', min_price: 0, has_discount: false };
            }
          })
        );

        if (cancelled) return;

        const discountMap = {};
        results.forEach(r => {
          discountMap[r.id] = r;
        });

        setProductDiscountInfo(discountMap);
        setDiscountInfoLoaded(true);
      } catch (error) {
        console.error("Error fetching prices:", error);
        setDiscountInfoLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [productList]);

  useEffect(() => {
    const nodes = Object.values(cardsRef.current).filter(Boolean);
    if (!nodes.length || !discountInfoLoaded) return;

    gsap.killTweensOf(nodes);
    gsap.fromTo(
      nodes,
      { opacity: 0, y: 18, scale: 0.98 },
      { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "power2.out", stagger: 0.06 }
    );
  }, [productList, discountInfoLoaded]);

  // FIXED: Get effective price with discount - Now properly handles empty values in arrays
  const getEffectivePrice = (product) => {
    const productId = String(product?.product_id ?? "");
    const discountData = productDiscountInfo[productId];

    if (!product) return { final: 0, original: 0, hasDiscount: false, percent: 0 };

    // Log the product data for debugging
    console.log(`Checking product ${productId}:`, {
      name: product.name,
      prices: product.prices,
      single_discount_types: product.single_discount_types,
      single_discount_values: product.single_discount_values
    });

    // Check if product has prices string with discounts (from getallproducts.php)
    if (product?.prices && product?.single_discount_types && product?.single_discount_values) {
      
      // Split the strings into arrays
      const priceArray = product.prices.split(',').map(p => parseFloat(p)).filter(p => !isNaN(p) && p > 0);
      const discountTypes = product.single_discount_types.split(',');
      const discountValues = product.single_discount_values.split(',').map(v => parseFloat(v));
      
      console.log(`Product ${productId} parsed:`, {
        priceArray,
        discountTypes,
        discountValues
      });

      if (priceArray.length > 0) {
        let lowestPrice = Infinity;
        let lowestOriginal = Infinity;
        let hasAnyDiscount = false;
        let bestDiscountPrice = null;
        let bestOriginalForDiscount = null;
        let bestDiscountPercent = 0;

        // Check each size for discounts
        priceArray.forEach((price, idx) => {
          const originalPrice = price;
          let finalPrice = originalPrice;
          
          // Check if this size has a valid discount
          const discountType = discountTypes[idx] ? discountTypes[idx].trim() : '';
          const discountValue = discountValues[idx] || 0;
          
          console.log(`Size ${idx}:`, {
            originalPrice,
            discountType,
            discountValue,
            hasDiscount: discountType !== '' && discountValue > 0
          });
          
          if (discountType !== '' && discountValue > 0) {
            if (discountType === 'percentage') {
              finalPrice = originalPrice * (1 - discountValue / 100);
            } else if (discountType === 'amount' || discountType === 'fixed') {
              finalPrice = originalPrice - discountValue;
            }
            
            // Ensure price doesn't go below 0
            finalPrice = Math.max(0, finalPrice);
            
            if (finalPrice < originalPrice) {
              hasAnyDiscount = true;
              
              // Track the best discounted price
              if (bestDiscountPrice === null || finalPrice < bestDiscountPrice) {
                bestDiscountPrice = finalPrice;
                bestOriginalForDiscount = originalPrice;
                bestDiscountPercent = Math.round(((originalPrice - finalPrice) / originalPrice) * 100);
              }
            }
          }
          
          // Track lowest overall price
          if (finalPrice < lowestPrice) {
            lowestPrice = finalPrice;
          }
          if (originalPrice < lowestOriginal) {
            lowestOriginal = originalPrice;
          }
        });

        console.log(`Product ${productId} result:`, {
          hasAnyDiscount,
          bestDiscountPrice,
          bestOriginalForDiscount,
          bestDiscountPercent,
          lowestPrice
        });

        // If any discount found, return the best one
        if (hasAnyDiscount && bestDiscountPrice !== null && bestOriginalForDiscount !== null) {
          return {
            final: bestDiscountPrice,
            original: bestOriginalForDiscount,
            hasDiscount: true,
            percent: bestDiscountPercent
          };
        }

        // No discounts, return lowest price
        return {
          final: lowestPrice,
          original: lowestPrice,
          hasDiscount: false,
          percent: 0
        };
      }
    }

    // Check if we have pre-calculated discount info from getProductPrices.php summary
    if (discountData?.has_discount && discountData.discounted_price && discountData.original_price) {
      return {
        final: parseFloat(discountData.discounted_price),
        original: parseFloat(discountData.original_price),
        hasDiscount: true,
        percent: discountData.discount_percent || Math.round(((parseFloat(discountData.original_price) - parseFloat(discountData.discounted_price)) / parseFloat(discountData.original_price)) * 100)
      };
    }

    // Check for prices string with discounts from getProductPrices.php
    if (discountData?.prices && discountData.single_discount_types && discountData.single_discount_values) {
      const priceArray = discountData.prices.split(',').map(p => parseFloat(p)).filter(p => !isNaN(p) && p > 0);
      const discountTypes = discountData.single_discount_types.split(',');
      const discountValues = discountData.single_discount_values.split(',').map(v => parseFloat(v));

      if (priceArray.length > 0) {
        let lowestPrice = Infinity;
        let hasAnyDiscount = false;
        let bestDiscountPrice = null;
        let bestOriginalForDiscount = null;
        let bestDiscountPercent = 0;

        priceArray.forEach((price, idx) => {
          const originalPrice = price;
          let finalPrice = originalPrice;
          
          const discountType = discountTypes[idx] ? discountTypes[idx].trim() : '';
          const discountValue = discountValues[idx] || 0;
          
          if (discountType !== '' && discountValue > 0) {
            if (discountType === 'percentage') {
              finalPrice = originalPrice * (1 - discountValue / 100);
            } else if (discountType === 'amount' || discountType === 'fixed') {
              finalPrice = originalPrice - discountValue;
            }
            
            finalPrice = Math.max(0, finalPrice);
            
            if (finalPrice < originalPrice) {
              hasAnyDiscount = true;
              
              if (bestDiscountPrice === null || finalPrice < bestDiscountPrice) {
                bestDiscountPrice = finalPrice;
                bestOriginalForDiscount = originalPrice;
                bestDiscountPercent = Math.round(((originalPrice - finalPrice) / originalPrice) * 100);
              }
            }
          }
          
          if (finalPrice < lowestPrice) {
            lowestPrice = finalPrice;
          }
        });

        if (hasAnyDiscount && bestDiscountPrice !== null) {
          return {
            final: bestDiscountPrice,
            original: bestOriginalForDiscount,
            hasDiscount: true,
            percent: bestDiscountPercent
          };
        }

        return {
          final: lowestPrice,
          original: lowestPrice,
          hasDiscount: false,
          percent: 0
        };
      }
    }

    // Fallback to simple price
    const priceValue = parseFloat(product?.min_price || product?.price || 0);
    return {
      final: priceValue,
      original: priceValue,
      hasDiscount: false,
      percent: 0
    };
  };

  // Format price with discount
  const formatPriceWithDiscount = (product) => {
    const priceInfo = getEffectivePrice(product);

    if (priceInfo.final === 0 || isNaN(priceInfo.final)) {
      return {
        display: <span className="text-xs sm:text-sm font-medium text-gray-500">Price on request</span>,
        value: 0,
        hasDiscount: false
      };
    }

    if (priceInfo.hasDiscount && priceInfo.final < priceInfo.original) {
      return {
        display: (
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="text-xs sm:text-sm font-semibold text-green-600">
              ₹{Math.round(priceInfo.final).toLocaleString("en-IN")}
            </span>
            <span className="text-xs text-gray-400 line-through">
              ₹{Math.round(priceInfo.original).toLocaleString("en-IN")}
            </span>
          </div>
        ),
        value: priceInfo.final,
        hasDiscount: true
      };
    }

    return {
      display: (
        <span className="text-xs sm:text-sm font-medium text-gray-900">
          ₹{Math.round(priceInfo.final).toLocaleString("en-IN")}
        </span>
      ),
      value: priceInfo.final,
      hasDiscount: false
    };
  };

  // Get discount badge
  const getDiscountBadge = (product) => {
    const priceInfo = getEffectivePrice(product);

    if (priceInfo.hasDiscount && priceInfo.percent > 0) {
      return (
        <div className="absolute top-2 left-2 z-10 bg-red-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
          {priceInfo.percent}% OFF
        </div>
      );
    }

    return null;
  };

  const toImgUrl = (val) => {
    if (!val || typeof val !== "string") return null;
    const s = val.trim();
    if (!s) return null;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    return `${imgLocation}/${s}`;
  };

  const getProductImages = (product) => {
    const raw = product?.img_path;
    if (!raw) return { primary: FALLBACK_IMG, secondary: null };

    if (typeof raw === "string" && (raw.startsWith("http://") || raw.startsWith("https://"))) {
      return { primary: raw, secondary: null };
    }

    if (typeof raw === "string") {
      const trimmed = raw.trim();

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          const primary = toImgUrl(parsed[0]) || FALLBACK_IMG;
          const secondary = toImgUrl(parsed[1]);
          return { primary, secondary: secondary && secondary !== primary ? secondary : null };
        }
      } catch (e) {}

      if (trimmed.includes(",")) {
        const parts = trimmed
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
        const primary = toImgUrl(parts[0]) || FALLBACK_IMG;
        const secondary = toImgUrl(parts[1]);
        return { primary, secondary: secondary && secondary !== primary ? secondary : null };
      }

      if (trimmed.includes("|")) {
        const parts = trimmed
          .split("|")
          .map((x) => x.trim())
          .filter(Boolean);
        const primary = toImgUrl(parts[0]) || FALLBACK_IMG;
        const secondary = toImgUrl(parts[1]);
        return { primary, secondary: secondary && secondary !== primary ? secondary : null };
      }

      const primary = toImgUrl(trimmed) || FALLBACK_IMG;
      return { primary, secondary: null };
    }

    return { primary: FALLBACK_IMG, secondary: null };
  };

  const openPopup = (msg) => {
    setPopupMessage(msg);
    setShowPopup(true);
  };

  const handleClosePopup = () => setShowPopup(false);

  const handleWishlistToggle = async (product) => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      alert("Please log in to manage your wishlist.");
      return;
    }

    const productId = String(product?.product_id ?? "");
    if (!productId) return;

    if (wishlistBusy[productId]) return;
    setWishlistBusy((prev) => ({ ...prev, [productId]: true }));

    const isWishlisted = !!wishlistAdded[productId];

    try {
      if (isWishlisted) {
        await postPhp(`${ApiURl}/removeWishlistItem.php`, {
          user_id: userId,
          product_id: productId,
        });

        setWishlistAdded((prev) => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });

        window.dispatchEvent(
          new CustomEvent("wishlist:changed", { detail: { productId, inWishlist: false } })
        );

        openPopup("Item Removed from Wishlist");
      } else {
        await postPhp(`${ApiURl}/addWishlistItem.php`, {
          user_id: userId,
          product_id: productId,
        });

        setWishlistAdded((prev) => ({ ...prev, [productId]: true }));

        window.dispatchEvent(
          new CustomEvent("wishlist:changed", { detail: { productId, inWishlist: true } })
        );

        openPopup("Item Added to Wishlist");
      }
    } catch (error) {
      console.error("Wishlist toggle failed:", error);
      alert("Wishlist update failed. Please try again.");
    } finally {
      setWishlistBusy((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  const onCardEnter = (key) => {
    const el = cardsRef.current[key];
    if (!el) return;
    gsap.to(el, {
      y: -10,
      boxShadow: "0px 45px 60px 0px rgba(0, 0, 0, 0.087)",
      duration: 0.22,
      ease: "power2.out",
    });
  };

  const onCardLeave = (key) => {
    const el = cardsRef.current[key];
    if (!el) return;
    gsap.to(el, {
      y: 0,
      boxShadow: "0px 15px 20px 0px rgba(0, 0, 0, 0.057)",
      duration: 0.22,
      ease: "power2.out",
    });
  };

  // Memoize the sorted product list with discount info
  const sortedProductList = useMemo(() => {
    if (!productList || productList.length === 0) return [];
    
    console.log("Sorting products with discount info:", productDiscountInfo);
    
    // Create a copy to avoid mutating the original
    return [...productList].sort((a, b) => {
      const priceInfoA = getEffectivePrice(a);
      const priceInfoB = getEffectivePrice(b);
      
      console.log(`Sorting: Product ${a.product_id} - hasDiscount: ${priceInfoA.hasDiscount}, percent: ${priceInfoA.percent}`);
      console.log(`Sorting: Product ${b.product_id} - hasDiscount: ${priceInfoB.hasDiscount}, percent: ${priceInfoB.percent}`);
      
      // First priority: discounted items come first
      if (priceInfoA.hasDiscount && !priceInfoB.hasDiscount) return -1;
      if (!priceInfoA.hasDiscount && priceInfoB.hasDiscount) return 1;
      
      // If both have discount or both don't have discount, sort by price (low to high)
      if (priceInfoA.final < priceInfoB.final) return -1;
      if (priceInfoA.final > priceInfoB.final) return 1;
      
      // If same price, sort by name
      const nameA = (a?.name || a?.product_name || '').toLowerCase();
      const nameB = (b?.name || b?.product_name || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      
      return 0;
    });
  }, [productList, productDiscountInfo]);

  const totalProducts = useMemo(() => sortedProductList?.length || 0, [sortedProductList]);

  // Show loading state while discount info is being fetched
  if (!discountInfoLoaded && productList.length > 0) {
    return (
      <div className="min-h-screen bg-[var(--user-bg,#E8D9D5)] pt-4 font-['Poppins',sans-serif] sm:pt-6 md:pt-8 lg:pt-12">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-lg font-medium tracking-[0.12em] uppercase text-neutral-900">
            EXPLORE THIS CATEGORY
          </h1>
          <div className="flex justify-center items-center py-20">
            <div className="text-gray-600">Loading products...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--user-bg,#E8D9D5)] pt-4 font-['Poppins',sans-serif] sm:pt-6 md:pt-8 lg:pt-12">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <h1 className="text-lg font-medium tracking-[0.12em] uppercase text-neutral-900">
            EXPLORE THIS CATEGORY
          </h1>
          {sortedProductList.length > 0 && (
            <div className="text-sm text-gray-600">
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                Discounted items shown first
              </span>
            </div>
          )}
        </div>

        <section className="py-4 sm:py-6 lg:py-8">
          {totalProducts === 0 ? (
            <div className="text-center py-10 sm:py-14">
              <p className="text-gray-600">No products available.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
              {sortedProductList.map((product, index) => {
                const productId = String(product?.product_id ?? "");
                const key = `products-${productId || "x"}-${index}`;

                const isWishlisted = !!wishlistAdded[productId];
                const isBusy = !!wishlistBusy[productId];

                const { primary, secondary } = getProductImages(product);
                const priceDisplay = formatPriceWithDiscount(product);
                const discountBadge = getDiscountBadge(product);

                return (
                  <div
                    key={key}
                    ref={(el) => (cardsRef.current[key] = el)}
                    onMouseEnter={() => onCardEnter(key)}
                    onMouseLeave={() => onCardLeave(key)}
                    className={`group relative bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 ${
                      priceDisplay.hasDiscount ? 'ring-1 ring-green-200' : ''
                    }`}
                  >
                    <div className="p-2 sm:p-3">
                      <div className="flex justify-between items-center mb-1 sm:mb-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-900">
                          {priceDisplay.display}
                        </span>

                        <button
                          type="button"
                          disabled={isBusy}
                          className={`text-sm sm:text-base ${
                            isWishlisted ? "text-red-500" : "text-gray-400"
                          } hover:text-red-500 transition-colors disabled:opacity-50`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleWishlistToggle(product);
                          }}
                          aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                          title={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                        >
                          ❤
                        </button>
                      </div>

                      <Link to={`/ProductOverview/${product?.product_id}`} className="block relative">
                        <div className="relative h-32 sm:h-36 md:h-40 lg:h-48 flex items-center justify-center bg-gray-50 rounded-md overflow-hidden">
                          {discountBadge}
                          <img
                            src={primary}
                            alt={product?.name || product?.product_name || "Product"}
                            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-200 ${
                              secondary ? "opacity-100 group-hover:opacity-0" : "opacity-100"
                            }`}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = FALLBACK_IMG;
                            }}
                          />

                          {secondary && (
                            <img
                              src={secondary}
                              alt={`${product?.name || product?.product_name || "Product"} - alt`}
                              className="absolute inset-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = primary || FALLBACK_IMG;
                              }}
                            />
                          )}
                        </div>
                      </Link>
                    </div>

                    <div className="px-2 sm:px-3 pb-2 sm:pb-3">
                      <div className="flex justify-between items-center">
                        <div className="text-xs sm:text-sm font-medium text-gray-900 truncate pr-2">
                          {product?.name || product?.product_name || `Product ${product.product_id}`}
                        </div>

                        <Link
                          to={`/ProductOverview/${product?.product_id}`}
                          className="text-gray-600 hover:text-gray-900 text-base sm:text-lg"
                          aria-label="View product"
                          title="View product"
                        >
                          🛒
                        </Link>
                      </div>

                      <div className="mt-1 sm:mt-2">
                        <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                          {product?.category_name || 'Product'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {showPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{popupMessage}</h2>
            <button
              onClick={handleClosePopup}
              className="w-full py-2 px-4 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductItems;
